"use strict";

function asText(value) {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(asText).filter(Boolean).join(" ");
  return String(value).trim();
}

export function caseNarrative(payload = {}) {
  return [
    payload.text,
    payload.description,
    payload.case_description,
    payload.narrative,
    payload.caseText,
    payload.caseDescription,
  ]
    .map(asText)
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function deriveFundLifecycleScope(payload = {}) {
  const text = caseNarrative(payload);
  const terms = [];
  if (/\bsubscriptions?\b|\bsubscribe\b/.test(text)) terms.push("subscriptions");
  if (/\bredemptions?\b|\bredeem\b/.test(text)) terms.push("redemptions");
  if (/\bcapital calls?\b/.test(text)) terms.push("capital calls");
  if (/\bdistributions\b|\bdistribution payments?\b/.test(text)) terms.push("distributions");
  const unique = Array.from(new Set(terms));
  const phrase = unique.length === 0
    ? "fund-unit lifecycle events"
    : unique.length === 1
      ? unique[0]
      : `${unique.slice(0, -1).join(", ")} and ${unique[unique.length - 1]}`;
  return { terms: unique, phrase };
}

export function humanizeReportInputLabel(value, kind = "generic") {
  const raw = asText(value);
  const key = raw.toLowerCase();
  const maps = {
    custody: {
      "": "not yet decided",
      unknown: "not yet decided",
      "not-decided": "not yet decided",
      "regulated-custodian": "regulated custodian",
      "ta/csd": "transfer agent or CSD",
      "self-custody": "self-custody wallet model",
    },
    venue: {
      "": "not yet decided",
      unknown: "not yet decided",
      "csd/ta-registered": "CSD or transfer-agent record environment",
      "ats/mtf": "ATS or MTF",
      dex: "decentralized exchange",
      otc: "controlled OTC transfers",
    },
    settlement: {
      "": "not yet decided",
      unknown: "not yet decided",
      "not-decided": "not yet decided",
      "not-material-now": "not material at this stage",
      "traditional-fiat-rails": "traditional fiat payment rails",
      "custodian-bank-settlement": "custodian or bank settlement",
      "stablecoin-tokenized-cash": "stablecoin or tokenized cash",
      "deposit-token-tokenized-deposit": "bank deposit token, tokenized deposit or institutional DvP rail",
      "t+0 atomic": "T+0 or atomic DvP target",
      "t+1/2": "T+1/T+2 securities settlement",
      "off-chain": "off-chain booking and payment",
    },
    liquidity: {
      "": "not yet decided",
      unknown: "not yet decided",
      none: "no secondary liquidity planned",
      "ad-hoc otc": "ad-hoc controlled OTC transfers",
      quarterly: "quarterly transfer windows",
      continuous: "continuous transfer intent",
    },
    disclosure: {
      "": "not yet decided",
      unknown: "not yet decided",
      exempt: "exemption-based disclosure route",
      prospectus: "prospectus route",
      kid: "key information document route",
    },
  };
  return maps[kind]?.[key] || raw || "not yet decided";
}

export function findRenderedReportDefects(textValue, payload = {}) {
  const text = asText(textValue);
  const lower = text.toLowerCase();
  const narrative = caseNarrative(payload);
  const defects = [];

  if (text.length < 500) {
    defects.push("rendered_report_missing_or_too_short");
  }

  if ((/\bcommercial paper\b|\bcp program(?:me)?\b|\bcp notes?\b/.test(narrative)) &&
      !/\bbond programme\b/.test(narrative) &&
      /\bconventional bond programme\b|\bexisting bond programme\b/.test(lower)) {
    defects.push("commercial_paper_as_bond_programme");
  }

  if (/money[- ]market fund|\bmmf\b|short-duration treasury fund/.test(narrative) &&
      !/capital calls?/.test(narrative) &&
      /capital calls?|kapitalabruf|kapitalindkald|kapitalkald|appels? de capitaux|llamadas? de capital|chamadas? de capital|资本调用/.test(lower)) {
    defects.push("money_market_fund_capital_calls");
  }

  const custody = asText(payload?.custodyModel || payload?.custody_model).toLowerCase();
  if ((!custody || /unknown|not[- ]decided|select/.test(custody)) &&
      /conservative custody(?:\s*\/\s*| and )settlement choices/.test(lower)) {
    defects.push("unsupported_conservative_custody");
  }

  const liquidity = asText(
    payload?.liquidityCadence ||
    payload?.secondaryMarketLiquidity ||
    payload?.secondary_market_liquidity
  ).toLowerCase();
  if (liquidity && !/^(?:unknown|not[- ]decided|select)$/.test(liquidity) &&
      /transfer or liquidity cadence is not stated/.test(lower)) {
    defects.push("structured_liquidity_reported_as_missing");
  }

  const exactDefects = [
    ["thin_disclosure_grammar", /thin disclosure architecture make\b/i],
    ["capitalized_mid_sentence", /cannot yet show that The proposed\b/],
    ["distribution_support_grammar", /capable of distribution support pool-performance/i],
    ["internal_custody_label", /Selected custody-design label:/i],
    ["internal_transfer_environment_label", /Generic launch transfer-environment label:/i],
  ];
  for (const [code, pattern] of exactDefects) {
    if (pattern.test(text)) defects.push(code);
  }

  for (const internalValue of [
    "traditional-fiat-rails",
    "stablecoin-tokenized-cash",
    "deposit-token-tokenized-deposit",
    "not-decided",
  ]) {
    if (lower.includes(internalValue)) defects.push(`internal_enum:${internalValue}`);
  }

  const missingCashEvidence =
    /no commitment|no committed|not committed|no finality analysis|no complete recovery|not yet (?:tested|validated)|no audited production/.test(narrative);
  // Only treat an Advanced/Strong readiness statement as a cash-leg claim when
  // the readiness wording is explicitly tied to the cash leg. The previous
  // whole-report pattern could incorrectly match unrelated phrases such as
  // “operational readiness is assessed as Advanced” in cases that also stated
  // missing cash-leg evidence elsewhere in the input.
  const overstatedReadiness =
    /cash[- ]leg[^.!?\n]{0,220}(?:readiness[^.!?\n]{0,80})?(?:advanced|strong|avanceret|stærk|fortgeschritten|stark|avancée|forte|avanzada|fuerte|avançada|高级|先进|强)/.test(lower) ||
    /(?:advanced|strong|avanceret|stærk|fortgeschritten|stark|avancée|forte|avanzada|fuerte|avançada|高级|先进|强)[^.!?\n]{0,120}cash[- ]leg/.test(lower);
  if (missingCashEvidence && overstatedReadiness) {
    defects.push("cash_leg_readiness_overstated");
  }

  return Array.from(new Set(defects));
}

export function assertRenderedReportQuality(textValue, payload = {}) {
  const defects = findRenderedReportDefects(textValue, payload);
  if (defects.length === 0) return true;

  // Only structural render failure is allowed to block report export.
  // Language- and phrase-based quality checks remain diagnostic only because
  // legitimate report wording can otherwise create false-positive hard stops.
  const blockingDefects = defects.filter((code) => code === "rendered_report_missing_or_too_short");
  const diagnosticDefects = defects.filter((code) => !blockingDefects.includes(code));
  if (diagnosticDefects.length > 0) {
    console.warn("TVAC rendered report quality diagnostics:", diagnosticDefects);
  }
  if (blockingDefects.length === 0) return true;

  const error = new Error(`TVAC rendered report quality failure: ${blockingDefects.join(", ")}`);
  error.code = "TVAC_RENDERED_REPORT_QUALITY_FAILURE";
  error.details = blockingDefects;
  throw error;
}

// Report-scope interpretation only. This never assigns factor scores or verdicts.
export function hasAffirmativeReportActivity(value, pattern) {
  return String(value || '').split(/[.!?;\n]+|\b(?:but|however|whereas)\b/i).some(clause => {
    const match = clause.match(pattern);
    if (!match) return false;
    const before = clause.slice(0, match.index).replace(/no (?:restrictions?|limits?|prohibition) (?:on|against)/gi, 'permitted');
    const after = clause.slice(match.index + match[0].length);
    if (/\b(?:no|not|without|never|exclude[ds]?|avoid(?:ed|ing)?|prohibit(?:ed|s)?)\b[^.!?;]{0,160}$/i.test(before)) return false;
    if (/^[^.!?;]{0,55}\b(?:is|are|would be|will be)?\s*(?:not (?:required|needed|planned|permitted|allowed|assumed)|out of scope|excluded|prohibited)\b/i.test(after)) return false;
    return true;
  });
}

export function isInvoiceFinanceWorkflow(payload = {}) {
  const text = ['text','description','case_description','caseDescription','narrative','caseText','caseInput','transferRestrictions'].map(k=>String(payload?.[k]||'')).join(' ');
  const hasInvoice = /\binvoice(?:s|[- ]financ(?:e|ing))?\b/i.test(text);
  const operational = /\b(?:assignment|funding status|financing status|funding lock|invoice[- ]state|invoice acceptance)\b/i.test(text);
  const bankFunded = /\bbanks?\b/i.test(text);
  const noPublicProduct = /\bno public (?:offering|investment)|\bnot a public investment product|\bno retail investment/i.test(text);
  const noTrading = /^(?:none|no|not planned)$/i.test(String(payload?.liquidityCadence || '')) || /\bno (?:atomic settlement or )?secondary trading\b|\bno[^.!?;]{0,85}\bsecondary trading\b|\bsecondary trading is not required\b/i.test(text);
  const capitalRaise = hasAffirmativeReportActivity(text, /\b(?:private placement|note issuance|fundraising|capital raise|offer(?:ing)? (?:to|for) investors)\b/i);
  return hasInvoice && operational && bankFunded && noPublicProduct && noTrading && !capitalRaise;
}

// Role semantics for archived invoice assessments; new reports supply role-level prose.
const invoiceRoleCopy = {
  "en": [
    "Banks funding the workflow",
    "Small suppliers receiving finance",
    "Buyers and infrastructure providers",
    "Potential value is better access to usable working capital and lower all-in borrower charges. These outcomes require evidence against conventional delivery; they are not established by a shared token record.",
    "Confirm total financing charges, repayment terms, data consent, correction and complaint procedures for suppliers. A bank process saving does not itself guarantee affordable or reliable finance.",
    "Buyers support invoice acceptance and repayment; providers deliver agreed infrastructure services. Any buyer benefit, provider fees and allocation of savings require contractual confirmation.",
    "Confirm responsibility for invoice acceptance, payment, data accuracy, access controls, service continuity, corrections and provider replacement. Provider revenue is not proof of value for suppliers or banks."
  ],
  "da": [
    "Banker, der finansierer processen",
    "Små leverandører, der modtager finansiering",
    "Købere og infrastrukturleverandører",
    "Mulig værdi er bedre adgang til brugbar arbejdskapital og lavere samlede finansieringsomkostninger. Det skal dokumenteres mod konventionel levering; et fælles tokenregister beviser det ikke.",
    "Bekræft leverandørernes samlede gebyrer, tilbagebetalingsvilkår, datasamtykke samt korrektions- og klageprocedure. Bankens procesbesparelse garanterer ikke billig eller pålidelig finansiering.",
    "Købere understøtter fakturaaccept og betaling; udbydere leverer aftalte infrastrukturtjenester. Købernes gevinst, udbydernes gebyrer og fordelingen af besparelser kræver aftalemæssig bekræftelse.",
    "Bekræft ansvar for fakturaaccept, betaling, datakvalitet, adgang, driftskontinuitet, rettelser og udbyderskifte. Udbyderomsætning beviser ikke værdi for leverandører eller banker."
  ],
  "de": [
    "Finanzierende Banken",
    "Finanzierte kleine Lieferanten",
    "Käufer und Infrastrukturanbieter",
    "Möglicher Nutzen sind besserer Zugang zu Betriebskapital und niedrigere Gesamtkosten der Finanzierung. Dies muss gegenüber konventioneller Bereitstellung belegt werden; ein Tokenregister beweist es nicht.",
    "Gesamtkosten, Rückzahlungsbedingungen, Datenzustimmung sowie Berichtigungs- und Beschwerdeverfahren für Lieferanten klären. Prozessersparnisse der Bank garantieren keine günstige oder zuverlässige Finanzierung.",
    "Käufer unterstützen Rechnungsannahme und Zahlung; Anbieter erbringen vereinbarte Infrastrukturleistungen. Vorteile, Anbietergebühren und die Verteilung von Einsparungen müssen vertraglich geklärt werden.",
    "Verantwortung für Rechnungsannahme, Zahlung, Datenqualität, Zugriff, Betriebskontinuität, Berichtigungen und Anbieterwechsel klären. Anbieterumsatz beweist keinen Mehrwert für Lieferanten oder Banken."
  ],
  "fr": [
    "Banques finançant le processus",
    "Petits fournisseurs financés",
    "Acheteurs et prestataires d’infrastructure",
    "La valeur potentielle est un meilleur accès au fonds de roulement et une baisse du coût total du financement. Ces résultats doivent être démontrés face au processus conventionnel ; un registre de tokens ne les prouve pas.",
    "Confirmer les frais totaux, le remboursement, le consentement aux données et les procédures de correction et de réclamation. Une économie bancaire ne garantit pas un financement abordable ou fiable.",
    "Les acheteurs participent à l’acceptation et au paiement des factures ; les prestataires livrent les services convenus. Avantages, frais et partage des économies doivent être confirmés contractuellement.",
    "Confirmer les responsabilités : acceptation, paiement, qualité des données, accès, continuité, corrections et remplacement du prestataire. Son chiffre d’affaires ne prouve pas la valeur pour les fournisseurs ou les banques."
  ],
  "es": [
    "Bancos que financian el proceso",
    "Pequeños proveedores financiados",
    "Compradores y proveedores de infraestructura",
    "El valor potencial es mejorar el acceso al capital de trabajo y reducir el coste total del crédito. Debe demostrarse frente al proceso convencional; un registro de tokens no lo prueba.",
    "Confirmar cargos totales, reembolso, consentimiento de datos y procedimientos de corrección y reclamación. El ahorro del banco no garantiza financiación asequible o fiable.",
    "Los compradores apoyan la aceptación y el pago de facturas; los prestadores entregan los servicios acordados. Beneficios, tarifas y reparto del ahorro requieren confirmación contractual.",
    "Confirmar responsabilidades de aceptación, pago, calidad de datos, acceso, continuidad, correcciones y sustitución del prestador. Sus ingresos no prueban valor para proveedores o bancos."
  ],
  "pt-BR": [
    "Bancos que financiam o processo",
    "Pequenos fornecedores financiados",
    "Compradores e provedores de infraestrutura",
    "O valor potencial é ampliar o acesso ao capital de giro e reduzir o custo total do crédito. Isso exige evidências contra a entrega convencional; um registro de tokens não o comprova.",
    "Confirmar encargos totais, pagamento, consentimento de dados e procedimentos de correção e reclamação. A economia do banco não garante financiamento acessível ou confiável.",
    "Compradores apoiam a aceitação e o pagamento de faturas; prestadores entregam os serviços acordados. Benefícios, tarifas e distribuição das economias exigem confirmação contratual.",
    "Confirmar responsabilidades por aceitação, pagamento, qualidade dos dados, acesso, continuidade, correções e substituição do prestador. Receita do prestador não comprova valor para fornecedores ou bancos."
  ],
  "zh-Hans": [
    "提供融资的银行",
    "获得融资的小供应商",
    "买方与基础设施提供商",
    "潜在价值是改善营运资金获取并降低借款总费用。这些结果必须与传统方案比较验证；共享代币记录本身不能证明。",
    "确认供应商的全部融资费用、还款条款、数据同意、更正和投诉程序。银行流程节省并不保证融资可负担或可靠。",
    "买方参与发票确认和付款；服务商交付约定的基础设施服务。买方收益、服务费用及节省分配需要合同确认。",
    "确认发票接受、付款、数据准确性、权限、业务连续性、更正和服务商替换责任。服务商收入不证明供应商或银行获得价值。"
  ]
};
export function getInvoiceStakeholderRoles(result = {}, language = "en") {
  const f = result.case_decision_frame || {};
  const c = invoiceRoleCopy[language] || invoiceRoleCopy.en;
  const first = value => Array.isArray(value) ? value.find(x => typeof x === "string" && x.trim()) : typeof value === "string" ? value : "";
  const fx = result.factor_explanations || {};
  const questions = Array.isArray(f.decisive_unknowns) ? f.decisive_unknowns : [];
  const question = pattern => questions.find(q => pattern.test(q)) || f.next_evidence_test || f.tokenization_test;
  return {
    asset_owner: {title:c[0],subtitle:f.tokenization_test,value:first(fx.cost_savings) || f.value_mechanism,burden:first(fx.tokenization_costs) || f.factor_baselines?.tokenization_costs,alignment:question(/registry|database|cost|omkost|coût|custo|Kosten|成本/i)},
    investors: {title:c[1],subtitle:f.beneficiaries,value:c[3],burden:c[4],alignment:question(/supplier|borrower|charges|leverand|fournisseur|proveedor|fornecedor|Lieferant|供应商/i)},
    infrastructure: {title:c[2],subtitle:c[5],value:c[5],burden:c[6],alignment:question(/provider|failure|contract|prestataire|contrat|Anbieter|服务商/i)},
  };
}
